import React, { useEffect } from "react";
import { useParams, Link, useSearch } from "@tanstack/react-router";
import { useAdminOrganizations } from "../hooks/useAdminOrganizations";
import { useHelpdeskNavigation } from "../hooks/useHelpdeskNavigation";
import { useOrganizationSettings } from "../hooks/useOrganizationSettings";
import { usePersistedFilters } from "../hooks/usePersistedFilters";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Building,
  Save,
  Loader2,
  AlertCircle,
  RotateCcw,
  Settings,
  Mail,
  Globe,
  Ticket,
  Users,
  FolderTree,
  Library,
  Zap,
  Trash2,
  MailWarning,
} from "lucide-react";
import { OrganizationSettingsSkeleton } from "../components/LoadingSkeleton";
import {
  OrgSettingsProvider,
  useOrgSettings,
  GeneralSettings,
  EmailSettings,
  PortalSettings,
  TicketSettings,
} from "../components/org-settings";
import { CategorySettings } from "../components/CategorySettings";
import { CollectionSettings } from "../components/CollectionSettings";
import { UserSettings } from "../components/UserSettings";
import { AutomationSettings } from "../components/AutomationSettings";
import { DeletedTicketsSettings } from "../components/DeletedTicketsSettings";
import { HeldEmailsSettings } from "../components/HeldEmailsSettings";
import type { OrgSettingsTab } from "../components/org-settings";

// Inner component that uses the context
function OrgSettingsContent() {
  const {
    organizationId,
    organizationName,
    isDirty,
    isSaving,
    saveSettings,
    resetFormState,
    isLoading,
    formState,
    emailValidation,
  } = useOrgSettings();

  const { getPath } = useHelpdeskNavigation();
  const { orgId } = useParams({ from: "/helpdesk/org-settings/$orgId" });
  const urlParams = useSearch({ from: "/helpdesk/org-settings/$orgId" });
  const { data: organizationSettings, isLoading: isLoadingSettings } =
    useOrganizationSettings(orgId);

  // Tab persistence
  const { updateFilters, isInitialized } = usePersistedFilters(
    `helpdesk.org-settings.${orgId}.tab`,
    { tab: "general" },
    urlParams
  );

  // Get the active tab from URL params, defaulting to "general"
  const activeTab = (urlParams.tab as OrgSettingsTab) || "general";

  // Handle tab change with unsaved changes warning
  const handleTabChange = (newTab: string) => {
    if (isDirty) {
      const confirmed = confirm(
        "You have unsaved changes. Are you sure you want to switch tabs? Your changes will be lost."
      );
      if (!confirmed) return;
      resetFormState();
    }
    updateFilters({ tab: newTab as OrgSettingsTab });
  };

  // Keyboard shortcut for save (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (isDirty && !isSaving) {
          saveSettings();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isDirty, isSaving, saveSettings]);

  // Determine if save should be disabled
  const canSave =
    isDirty &&
    !isSaving &&
    !emailValidation.isValidating &&
    emailValidation.isValid !== false &&
    formState.fromEmailPrefix.trim() !== "" &&
    formState.fromEmailName.trim() !== "";

  // Show skeleton while loading
  if (!isInitialized || isLoading || isLoadingSettings) {
    return (
      <div className="space-y-4 md:space-y-6">
        <OrganizationSettingsSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-start sm:items-center gap-3 sm:gap-4">
        <Link to={getPath.orgSettings()}>
          <Button variant="outline" size="icon" className="flex-shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <Building className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 hidden sm:block" />
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                {organizationName || "Organization Settings"}
              </h1>
            </div>
            {/* Desktop logo */}
            {organizationSettings?.logoUrl && (
              <div className="hidden sm:block p-2 rounded-lg dark:bg-white/35 flex-shrink-0">
                <img
                  src={organizationSettings.logoUrl}
                  alt={`${organizationName || "Organization"} logo`}
                  className="h-10 w-auto object-contain"
                />
              </div>
            )}
          </div>
          {/* Mobile logo */}
          {organizationSettings?.logoUrl && (
            <div className="sm:hidden p-2 rounded-lg dark:bg-white/35 w-fit">
              <img
                src={organizationSettings.logoUrl}
                alt={`${organizationName || "Organization"} logo`}
                className="h-10 w-auto object-contain"
              />
            </div>
          )}
        </div>
      </div>

      {/* Vertical Tabs Layout */}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex flex-col lg:flex-row lg:items-start gap-6"
        orientation="vertical"
      >
        {/* Sidebar Navigation */}
        <TabsList className="grid grid-cols-4 sm:grid-cols-10 lg:grid-cols-1 h-auto w-full lg:w-56 lg:min-w-56 lg:max-w-56 p-1 gap-1 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg shrink-0 lg:self-start">
          <TabsTrigger
            value="general"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <Settings className="h-4 w-4" />
            <span>General</span>
          </TabsTrigger>
          <TabsTrigger
            value="email"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <Mail className="h-4 w-4" />
            <span>Email</span>
          </TabsTrigger>
          <TabsTrigger
            value="portal"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">Customer Portal</span>
            <span className="sm:hidden">Portal</span>
          </TabsTrigger>
          <TabsTrigger
            value="tickets"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <Ticket className="h-4 w-4" />
            <span className="hidden sm:inline">Tickets & SLA</span>
            <span className="sm:hidden">Tickets</span>
          </TabsTrigger>
          <TabsTrigger
            value="users"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <Users className="h-4 w-4" />
            <span>Users</span>
          </TabsTrigger>
          <TabsTrigger
            value="categories"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <FolderTree className="h-4 w-4" />
            <span>Categories</span>
          </TabsTrigger>
          <TabsTrigger
            value="collections"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <Library className="h-4 w-4" />
            <span className="hidden sm:inline">KB Collections</span>
            <span className="sm:hidden">Collections</span>
          </TabsTrigger>
          <TabsTrigger
            value="automation"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <Zap className="h-4 w-4" />
            <span>Automation</span>
          </TabsTrigger>
          <TabsTrigger
            value="deleted"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Deleted Tickets</span>
            <span className="sm:hidden">Deleted</span>
          </TabsTrigger>
          <TabsTrigger
            value="held"
            className="flex-col sm:flex-row lg:flex-row lg:justify-start gap-1 sm:gap-2 px-2 sm:px-3 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 text-xs sm:text-sm"
          >
            <MailWarning className="h-4 w-4" />
            <span className="hidden sm:inline">Held Emails</span>
            <span className="sm:hidden">Held</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab Content Area */}
        <div className="flex-1 min-w-0 lg:max-w-[calc(100%-15rem)]">
          {/* Save/Reset Actions Bar - shown for settings tabs only */}
          {activeTab !== "users" && activeTab !== "categories" && activeTab !== "collections" && activeTab !== "automation" && activeTab !== "deleted" && activeTab !== "held" && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                {isDirty ? (
                  <>
                    <span className="h-2 w-2 rounded-full bg-yellow-500 flex-shrink-0" />
                    <span className="hidden sm:inline">You have unsaved changes</span>
                    <span className="sm:hidden">Unsaved changes</span>
                  </>
                ) : (
                  <>
                    <span className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
                    <span className="hidden sm:inline">All changes saved</span>
                    <span className="sm:hidden">Saved</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFormState}
                  disabled={!isDirty || isSaving}
                  className="flex-1 sm:flex-none"
                >
                  <RotateCcw className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Reset</span>
                </Button>
                <Button
                  size="sm"
                  onClick={saveSettings}
                  disabled={!canSave}
                  className="flex-1 sm:flex-none"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 sm:mr-2" />
                  )}
                  <span>Save</span>
                </Button>
              </div>
            </div>
          )}

          {/* Tab Contents */}
          <TabsContent value="general" className="mt-0">
            <GeneralSettings />
          </TabsContent>

          <TabsContent value="email" className="mt-0">
            <EmailSettings />
          </TabsContent>

          <TabsContent value="portal" className="mt-0">
            <PortalSettings />
          </TabsContent>

          <TabsContent value="tickets" className="mt-0">
            <TicketSettings />
          </TabsContent>

          <TabsContent value="users" className="mt-0">
            <UserSettings organizationId={organizationId} />
          </TabsContent>

          <TabsContent value="categories" className="mt-0">
            <CategorySettings organizationId={organizationId} />
          </TabsContent>

          <TabsContent value="collections" className="mt-0">
            <CollectionSettings organizationId={organizationId} />
          </TabsContent>

          <TabsContent value="automation" className="mt-0">
            <AutomationSettings organizationId={organizationId} />
          </TabsContent>

          <TabsContent value="deleted" className="mt-0">
            <DeletedTicketsSettings organizationId={organizationId} />
          </TabsContent>

          <TabsContent value="held" className="mt-0">
            <HeldEmailsSettings organizationId={organizationId} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// Main component that wraps with provider
export const OrgSettingsDetail: React.FC = () => {
  const { orgId } = useParams({ from: "/helpdesk/org-settings/$orgId" });
  const { data: adminOrgs, isLoading: isLoadingOrgs } = useAdminOrganizations();
  const currentOrg = adminOrgs?.find((org) => org.id === orgId);

  // Check admin access
  if (!isLoadingOrgs && !currentOrg) {
    return (
      <div className="space-y-4 md:space-y-6">
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              You don't have admin access to this organization.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoadingOrgs) {
    return (
      <div className="space-y-4 md:space-y-6">
        <OrganizationSettingsSkeleton />
      </div>
    );
  }

  return (
    <OrgSettingsProvider organizationId={orgId}>
      <OrgSettingsContent />
    </OrgSettingsProvider>
  );
};
